import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { absUrl } from "@/lib/seo-jsonld";
import {
  CREDIT_LINE,
  CREATOR_ALIAS,
  DOCUMENT_VERSION,
  ORIGINAL_CREATION_DATE,
  ORIGINAL_CREATION_DATE_ISO,
  ORIGINAL_CREATION_DATE_NUMERIC,
  VERSION_BADGE,
} from "@/lib/document-credits";

const PDF_HREF = "/docs/SFN_Network_Master_Whitepaper_v1.0.pdf";


export const Route = createFileRoute("/whitepaper")({
  component: WhitepaperPage,
  head: () => ({
    meta: [
      { title: "SFN Network Master Whitepaper v1.0 — MealToken24" },
      {
        name: "description",
        content:
          "A Dual-RWA ecosystem for social balance, real utility and sustainable yield on Solana — the SFN Network Master Whitepaper v1.0.",
      },
      { property: "og:title", content: "SFN Network Master Whitepaper v1.0" },
      {
        property: "og:description",
        content:
          "Freezing the price of food: 5-layer Dual-RWA architecture, MEAL2/MEAL4 tokenomics and the phased execution roadmap.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absUrl("/whitepaper") },
      { property: "og:image", content: absUrl("/og-image.jpg") },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absUrl("/og-image.jpg") },
    ],
    links: [{ rel: "canonical", href: absUrl("/whitepaper") }],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function Bullet({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <li className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <strong className="text-foreground">{term}</strong> — {children}
    </li>
  );
}

function WhitepaperPage() {
  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to SFN
          </Link>
          <a
            href={PDF_HREF}
            download="SFN_Network_Master_Whitepaper_v1.0.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-saffron to-coral px-4 py-2 text-xs font-bold uppercase tracking-wide text-obsidian shadow-[var(--glow-saffron)]"
          >
            <Download className="h-3.5 w-3.5" />
            PDF
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-saffron">
          // SFN · WHITEPAPER · {DOCUMENT_VERSION.toUpperCase()}
        </span>
        <h1 className="mt-3 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
          Freezing the Price of Food.
          <br />
          <span className="italic text-gradient-saffron">
            A Dual-RWA Ecosystem for Social Balance, Real Utility, and Sustainable Yield on Solana.
          </span>
        </h1>
        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <span className="inline-block rounded-full border border-saffron/40 bg-saffron/10 px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-saffron">
            {VERSION_BADGE}
          </span>
          <p className="mt-3">
            Author &amp; Creator: <span className="text-foreground">{CREATOR_ALIAS}</span>
          </p>
          <p className="mt-1">
            Original Creation Date:{" "}
            <time dateTime={ORIGINAL_CREATION_DATE_ISO} className="text-foreground">
              {ORIGINAL_CREATION_DATE}
            </time>{" "}
            ({ORIGINAL_CREATION_DATE_NUMERIC})
          </p>
          <p className="mt-1">{CREDIT_LINE}</p>
        </div>

        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
          The Sustainable Food & Eco Network (SFN) is a real-world utility protocol combining solar-powered
          kitchens, blockchain-anchored asset reserves, and a dual-token economy designed to defeat
          food inflation in vulnerable regions.
        </p>

        <Section title="1. The Problem">
          <p>
            Global food inflation has outpaced wages for the third year running. In rural Southeast
            Asia, a balanced plate that cost $0.80 in 2020 now exceeds $1.80 — a doubling that
            fractures household stability and accelerates malnutrition.
          </p>
        </Section>

        <Section title="2. The SFN Solution">
          <p>
            SFN converts donations and token purchases into bulk-sourced ingredients, solar
            infrastructure, and operational reserves at partner kitchens. Tokens act as redemption
            certificates: every MEAL2 represents the right to a physical, audited plate.
          </p>
        </Section>

        <Section title="3. The 5-Layer Ecosystem & Dual-RWA Architecture">
          <ul className="space-y-3">
            <Bullet term="PlantyPando ($PPANDO)">
              Automated land acquisition via hard-coded 2% transfer fee.
            </Bullet>
            <Bullet term="AquaPando ($AQUA)">
              Solar-powered water management and irrigation throughput.
            </Bullet>
            <Bullet term="SolarPando ($PANDO)">
              Industrial solar infrastructure with an automated 5% hardware reinvestment tax.
            </Bullet>
            <Bullet term="SFN MEAL System (MEAL2 &amp; MEAL4)">
              MEAL2 ($1 / ~30 THB anchor) for daily payments; MEAL4 (capped 1M supply) as the
              deflationary inflation shield with revenue buyback-and-burn.
            </Bullet>
            <Bullet term="VillagePando">
              1:1 USDC-pegged RWA vouchers and smart settlement infrastructure.
            </Bullet>
          </ul>
        </Section>

        <Section title="4. Resource Distribution">
          <p>
            40% Market Stability &amp; Fair Price Supply · 30% SFN Social Aid &amp; Loyalty Program ·
            20% Infrastructure Expansion &amp; Solar Power · 10% Operational Safety &amp;
            Development.
          </p>
        </Section>

        <Section title="5. Phased Execution Roadmap">
          <ul className="space-y-3">
            <Bullet term="Phase 1 (August 2026)">
              Mainnet launch of SFN Cockpit, initial token sale for Thailand Sandbox Central Kitchen Hub
              &amp; Express Branches.
            </Bullet>
            <Bullet term="Phase 2 (Q4 2026)">
              Deployment of $PPANDO, $PANDO, and $AQUA; activation of 5% Rust tax routing program
              for solar hardware and land acquisition.
            </Bullet>
            <Bullet term="Phase 3 (2027)">
              B2B supplier integration (MEAL3) and regional franchise scaling (MEAL5).
            </Bullet>
          </ul>
        </Section>

        <Section title="6. Security & Trust">
          <p>
            All asset reserves are publicly auditable on Solscan. Smart contracts are immutable. The
            Freeze Authority for MEAL4 has been irrevocably destroyed.
          </p>
        </Section>

        <Section title="7. Disclaimer">
          <p>
            MEAL2 and MEAL4 are utility and asset-backed community tokens designed for practical use
            within the Sustainable Food & Eco Network gastronomy ecosystem. They do not constitute financial
            investments or traditional securities.
          </p>
        </Section>
      </main>

      <footer className="border-t border-white/10 px-6 py-10 text-center text-xs text-muted-foreground">
        <p>{CREDIT_LINE}</p>
        <p className="mt-1">{VERSION_BADGE}</p>
        <p className="mt-1">© {new Date().getFullYear()} SFN · MealToken24 — Document {DOCUMENT_VERSION}</p>
      </footer>
    </div>
  );
}
